import { useQuery, useQueryCache } from '@pinia/colada';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { fetchPurchaseOrders } from '@/services/purchaseOrderService';
import type { PurchaseOrder } from '@/types/purchaseOrders';
import { extractErrorMessage } from '@/utils/apiError';

export const ODOO_PO_STATES = [
  { value: 'draft', label: 'RFQ' },
  { value: 'sent', label: 'RFQ sent' },
  { value: 'purchase', label: 'Confirmed' },
  { value: 'done', label: 'Locked' },
] as const;

const DEFAULT_STATES = ['purchase'];

// The PO read hits Odoo live on the backend, and Odoo changes slowly — each filter
// combination is cached for 10 minutes. Our own capacity writes invalidate the cache
// explicitly (see capacityCalendarStore), so remaining quantities never outlive a write.
export const PURCHASE_ORDERS_QUERY_KEY = 'purchase-orders';
const MS_PER_MINUTE = 60_000;
const PO_STALE_MINUTES = 10;

export const usePurchaseOrderStore = defineStore('purchaseOrders', () => {
  const states = ref<string[]>([...DEFAULT_STATES]);
  const search = ref('');
  const schedulableForType = ref<string | undefined>(undefined);
  const vendorId = ref<string | undefined>(undefined);

  const queryCache = useQueryCache();

  function queryKey(): [string, string, string, string, string] {
    return [PURCHASE_ORDERS_QUERY_KEY, vendorId.value ?? '', states.value.join(','), search.value, schedulableForType.value ?? ''];
  }

  // enabled: false — entries load only through explicit load() calls, never as a side
  // effect of a consumer mutating filters (a vendorless, searchless fetch would 400).
  const query = useQuery<PurchaseOrder[]>({
    key: queryKey,
    query: () =>
      fetchPurchaseOrders({
        vendorId: vendorId.value,
        states: states.value,
        search: search.value || undefined,
        schedulableForType: schedulableForType.value,
      }),
    staleTime: PO_STALE_MINUTES * MS_PER_MINUTE,
    enabled: false,
  });

  const items = computed<PurchaseOrder[]>({
    get: () => (query.error.value ? [] : (query.data.value ?? [])),
    // Seeds the entry for the current filters — optimistic updates and test setup.
    set: (value) => queryCache.setQueryData(queryKey(), value),
  });
  const loading = query.isLoading;
  const error = computed(() => (query.error.value ? extractErrorMessage(query.error.value, 'Failed to load purchase orders') : null));

  // refresh() (not refetch()) honors staleTime: a repeat load within the window serves
  // the cached list with no Odoo round trip.
  async function load(id?: string): Promise<void> {
    vendorId.value = id;
    await query.refresh();
  }

  function reset(): void {
    vendorId.value = undefined;
    states.value = [...DEFAULT_STATES];
    search.value = '';
    schedulableForType.value = undefined;
  }

  return { items, loading, error, states, search, schedulableForType, load, reset };
});
